// headTrackIndicatorState — what the head-tracking viewport pill says, and
// which actions it offers. One pure function, so the decision is testable
// without a browser, a webcam, or MediaPipe. Sibling of `voiceInputState.js`
// (the mic control's equivalent) — same shape, same reason for existing.
//
// The availability rule follows this repo's convention (voiceInputState,
// DockedShell's rail, AppMenuBar): a control that cannot currently act is
// LISTED and explains why, never hidden — `blocked`/`error` render the real
// reason plus a retry action, they never disappear.
//
// `idle` is the one state that legitimately renders nothing: it means there
// is no `headTrackedCamera` node in the live graph, so there is no user
// intent to serve (ENG-981 Task 6 brief).

/** Fallback text when a start failure carries no message at all. */
export const UNKNOWN_ERROR_TEXT = 'unknown error'

/**
 * Shown instead of the normal 'armed' pill when tracking would run with
 * nowhere to apply its offset (whole-branch review, Important #1):
 * cameraController.apply() — the only reader of the head-offset source — is
 * driven exclusively by <CameraControllerSync>, which Scene.jsx mounts only
 * when `live && cameraRig === 'ok'`. Outside that, enabling the node would
 * still open the webcam (start() has no way to know about Scene/React
 * state, by design — see headTrackedCamera.js's header) with the offset
 * applied nowhere, ever: the exact "camera light on for no reason" failure
 * ENG-981 exists to avoid, reached through mode/rig absence instead of a
 * leak. Named causes only — never a generic "unavailable".
 */
export const NO_EFFECT_REASON = {
  NOT_LIVE: 'not in live mode',
  NO_RIG: 'no camera rig in the blueprint',
}

/**
 * Derive the head-tracking pill's presentation.
 *
 * @param {object}  opts
 * @param {string}  opts.state         headTrackedCamera.state
 * @param {boolean} [opts.faceSeen]    headTrackedCamera.faceSeen (only meaningful while 'tracking')
 * @param {string?} [opts.errorMessage] headTrackedCamera.errorMessage (only meaningful while 'error')
 * @param {boolean} [opts.effectPossible] whether cameraController.apply() would actually
 *   consume the head-offset source right now (live mode AND an 'ok' camera rig
 *   in the blueprint — see NO_EFFECT_REASON above). Defaults to true so every
 *   existing caller/test that doesn't pass it keeps today's behaviour.
 * @param {string?} [opts.noEffectReason] one of NO_EFFECT_REASON's values, naming
 *   the cause; only consulted when effectPossible is false.
 * @returns {{visible: boolean, tone: string|null, label: string|null,
 *            dot: 'solid'|'hollow'|null, actions: Array<{id: string, label: string}>}}
 *   `actions[].id` is one of 'start' | 'stop' | 'recenter' | 'retry' — the
 *   renderer maps each straight to the matching headTrackedCamera method.
 */
export function resolveHeadTrackIndicator({
  state,
  faceSeen = false,
  errorMessage = null,
  effectPossible = true,
  noEffectReason = null,
} = {}) {
  switch (state) {
    // 'armed' renders NO pill (2026-08-13, same session as blocked/error
    // below): an enabled node parked a permanent "Enable head tracking"
    // banner in the viewport of everyone who loads that blueprint. The tool
    // rail's parallax button is the start affordance instead
    // (resolveParallaxRailState 'armed' -> action 'start', which keeps the
    // NODE's config — unlike the idle click, which starts the preset view
    // session); when permission is already granted, arm() auto-starts and
    // the tracking chip appears on its own.
    case 'starting':
      return {
        visible: true,
        tone: 'neutral',
        label: 'Starting camera…',
        dot: null,
        actions: [],
      }
    case 'tracking': {
      // A session already tracking with nowhere to apply (e.g. started in the
      // live view, then the user switched to the editor — apply() no longer
      // runs) must SAY so, not show a healthy pill over a camera that will
      // never move. Same named-cause rule as the armed gate above; the stop
      // action stays (the ticket's easy off switch — the webcam IS still on),
      // recenter is dropped since there is no applied offset to re-zero.
      if (!effectPossible) {
        const reason = noEffectReason || 'no effect in the current view'
        return {
          visible: true,
          tone: 'warn',
          label: `Head tracking on — but no effect here (${reason})`,
          dot: faceSeen ? 'solid' : 'hollow',
          actions: [{ id: 'stop', label: '⨯' }],
        }
      }
      return {
        visible: true,
        tone: 'tracking',
        label: faceSeen ? 'Head tracking' : 'Head tracking — no face',
        dot: faceSeen ? 'solid' : 'hollow',
        // The "easy off switch" the ticket requires, plus recenter — both
        // available the whole time tracking is live.
        actions: [
          { id: 'recenter', label: 'Recenter' },
          { id: 'stop', label: '⨯' }, // stop button ×
        ],
      }
    }
    // 'blocked' and 'error' render NO pill. A permanently parked "camera
    // blocked" banner in the viewport punishes everyone who never uses the
    // feature (Andres, 2026-08-13: "not everyone uses camera and we don't
    // need to show this all the time"). The listed-with-reason convention is
    // served by the tool rail's parallax button instead: it carries a warn
    // mark + the reason in its tooltip while blocked/errored, and clicking it
    // retries — see ToolRail (DockedShell.jsx). resolveParallaxRailState
    // below is that button's decision, kept in this module so the two
    // surfaces can't drift apart about what each state means.
    case 'blocked':
    case 'error':
    case 'idle':
    default:
      return { visible: false, tone: null, label: null, dot: null, actions: [] }
  }
}

/**
 * resolveParallaxRailState — what the tool rail's parallax view-mode button
 * shows for a given runtime state. Same pure-decision pattern as the pill
 * resolver above, and deliberately in the same module: 'blocked'/'error'
 * moved OFF the pill and onto this button, so the two decisions form one
 * contract.
 *
 * @returns {{on: boolean, warn: boolean, title: string, action: string}}
 *   `on` lights the button (session live/coming up); `warn` marks it amber
 *   (blocked/error — the reason is in `title`, and a click retries).
 *   `action` is what a click does: 'stop' | 'start' | 'startViewSession'.
 *   The armed/blocked/error cases use 'start' — it KEEPS whatever config
 *   armed the runtime (a node's values, or a blocked view session's preset,
 *   since `viewSession` survives 'blocked') — while only a cold 'idle'
 *   click applies VIEW_MODE_PRESET via 'startViewSession'.
 */
export function resolveParallaxRailState({ state, errorMessage = null } = {}) {
  switch (state) {
    case 'starting':
    case 'tracking':
      return { on: true, warn: false, action: 'stop', title: 'Parallax view mode — on (click to turn off)' }
    // A blueprint node armed the runtime but tracking hasn't started (no
    // granted permission yet). This button is the start affordance — the
    // viewport deliberately shows no "enable" pill for it (see
    // resolveHeadTrackIndicator's armed case).
    case 'armed':
      return {
        on: false,
        warn: false,
        action: 'start',
        title: 'Parallax view mode — a Head-Tracked Camera node is enabled; click to start (uses your camera)',
      }
    case 'blocked':
      return {
        on: false,
        warn: true,
        action: 'start',
        title: 'Parallax view mode — camera blocked. Allow camera access for this site, then click to retry.',
      }
    case 'error':
      return {
        on: false,
        warn: true,
        action: 'start',
        title: `Parallax view mode — failed: ${errorMessage || UNKNOWN_ERROR_TEXT}. Click to retry.`,
      }
    default:
      return { on: false, warn: false, action: 'startViewSession', title: 'Parallax view mode (3D via your webcam)' }
  }
}
